import React, { useEffect, useState } from "react";
import { Form, Select, Upload, Button } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import "./FooterDocumentSettings.scss";
import { getRequest, postRequest, putRequest } from "../../api/http";
import userServiceApi from "../../api/userServiceApi";
import DeleteModal from "../deleteModal/deleteModal";

const documentFields = [
  {
    label: "Privacy Policy",
    name: "privacyPolicy",
    fileField: "privacyPolicyFile",
  },
  {
    label: "Terms of Use",
    name: "termsOfUse",
    fileField: "termsOfUseFile",
  },
  {
    label: "Legal",
    name: "legal",
    fileField: "legalFile",
  },
  {
    label: "Cookie Policy",
    name: "cookiePolicy",
    fileField: "cookiePolicyFile",
  },
];

const documentFieldMap = {
  "Privacy Policy": {
    name: "privacyPolicy",
    fileField: "privacyPolicyFile",
  },
  "Terms of Use": {
    name: "termsOfUse",
    fileField: "termsOfUseFile",
  },
  Legal: {
    name: "legal",
    fileField: "legalFile",
  },
  "Cookie Policy": {
    name: "cookiePolicy",
    fileField: "cookiePolicyFile",
  },
};

const FooterDocumentSettings = ({ partnerId }) => {
  const [footerDocumentForm] = Form.useForm();
  const allValues = Form.useWatch([], footerDocumentForm);

  const [selectedDocs, setSelectedDocs] = useState({});
  const [documentIds, setDocumentIds] = useState({});

  const [deleteFooterFileModal, setDeleteFooterFileModal] = useState(false);
  const [currentDeleteFileData, setCurrentDeleteFileData] = useState(null);
  const [loading, setLoading] = useState(false);

  const isSubmitDisabled = !documentFields.some(
    (item) => allValues?.[item.fileField]?.length,
  );

  const fetchFooterDocuments = async () => {
    try {
      const { data } = await getRequest(
        userServiceApi.fetchFooterDocuments(partnerId),
      );
      const formValues = {};
      const ids = {};
      const selectedValues = {};

      data?.forEach((doc) => {
        const field = documentFieldMap[doc?.title];

        if (!field) return;

        ids[field.name] = doc?.id;

        if (doc?.documentType === "FILE") {
          formValues[field.name] = "upload_document";

          formValues[field.fileField] = [
            {
              id: doc?.id,
              uid: doc?.id,
              name:
                doc?.fileName ||
                doc?.fileUrl?.split("/")?.pop() ||
                `${doc?.title}.pdf`,
              status: "done",
              label: doc?.title,
              url: doc?.fileUrl,
            },
          ];

          selectedValues[field.name] = "upload_document";
        }

        if (doc?.documentType === "TEXT") {
          formValues[field.name] = "add_manually_document";
          formValues[`${field.name}Text`] = doc?.textContent;

          selectedValues[field.name] = "add_manually_document";
        }
      });

      setDocumentIds(ids);
      setSelectedDocs(selectedValues);

      footerDocumentForm.setFieldsValue(formValues);
    } catch (error) {
      toast.error(error);
    }
  };

  const handleDocumentTypeChange = (field, value) => {
    setSelectedDocs((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);
      console.log("Form Values====>", values);

      const formData = new FormData();
      formData.append("partnerId", partnerId);

      const documents = [];
      let fileIndex = 0;

      documentFields.forEach((item) => {
        const documentType = values[item.name];

        if (!documentType) return;

        const existingId = documentIds[item.name] || null;

        if (
          documentType === "upload_document" &&
          values[item.fileField]?.length
        ) {
          const uploadedFile = values[item.fileField][0];

          // Existing file loaded from API
          const isExistingFile =
            uploadedFile?.status === "done" && !uploadedFile?.originFileObj;

          // User selected a new file
          const isNewOrModifiedFile = !!uploadedFile?.originFileObj;

          // Existing file not modified
          if (isExistingFile) {
            return;
          }

          if (isNewOrModifiedFile) {
            documents.push({
              id: existingId,
              title: item.label,
              documentType: "FILE",
              fileReferenceKey: String(fileIndex),
            });

            formData.append(
              String(fileIndex),
              uploadedFile.originFileObj,
              uploadedFile.name,
            );

            fileIndex++;
          }
        }

        if (documentType === "add_manually_document") {
          documents.push({
            id: existingId,
            title: item.label,
            documentType: "TEXT",
            textContent: values[`${item.name}Text`] || "",
          });
        }
      });

      if (documents?.length === 0) {
        toast.error("No changes detected!");
        setLoading(false);
        return;
      }

      formData.append(
        "documents",
        new Blob([JSON.stringify(documents)], {
          type: "application/json",
        }),
      );

      const response = await postRequest(
        userServiceApi.footerDocumentsUpload(),
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (response) {
        toast.success("Footer documents uploaded successfully");
        setLoading(false);
        fetchFooterDocuments();
      } else {
        console.log("Error response: ", response);
        setLoading(false);
        toast.error("Something went wrong");
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
      toast.error(error?.message || "Something went wrong");
    }
  };

  useEffect(() => {
    if (partnerId) {
      fetchFooterDocuments();
    }
  }, [partnerId]);

  const closeDeleteModal = () => {
    setCurrentDeleteFileData(null);
    setDeleteFooterFileModal(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      const file = currentDeleteFileData;
      // Existing file
      if (file?.id) {
        const response = await putRequest(
          userServiceApi.deleteFooterDocument(partnerId, file?.id),
        );
        if (response) {
          toast.success("Document deleted successfully");
          footerDocumentForm.setFieldValue(file?.fieldName, []);
          footerDocumentForm.setFieldValue(file?.footerFieldName, undefined);
          fetchFooterDocuments();
        }
      } else {
        footerDocumentForm.setFieldValue(file?.footerFieldName, undefined);
        footerDocumentForm.setFieldValue(file?.fieldName, []);
      }

      closeDeleteModal();
    } catch (error) {
      toast.error(error ?? "Failed to delete document");
    }
  };

  return (
    <Form
      form={footerDocumentForm}
      layout="vertical"
      onFinish={onFinish}
      autoComplete="off"
      className="footer-document-form"
    >
      {documentFields?.map((item) => (
        <div className="configureFormRow" key={item?.name}>
          <div className="configureFormCol">{item?.label}</div>

          <div className="configureFormCol">
            <Form.Item name={item?.name} style={{ minWidth: "100%" }}>
              <Select
                placeholder="Select Document Type"
                onChange={(value) =>
                  handleDocumentTypeChange(item?.name, value)
                }
                options={[
                  {
                    label: "Upload Document",
                    value: "upload_document",
                  },
                  // {
                  //   label: "Add Document Manually",
                  //   value: "add_manually_document",
                  // },
                ]}
              />
            </Form.Item>

            {selectedDocs[item?.name] === "upload_document" && (
              <Form.Item
                name={item?.fileField}
                valuePropName="fileList"
                getValueFromEvent={(e) => e?.fileList}
              >
                <div className="file-upload-container">
                  <Upload
                    accept=".pdf"
                    maxCount={1}
                    fileList={
                      footerDocumentForm.getFieldValue(item?.fileField) || []
                    }
                    onRemove={(file) => {
                      setCurrentDeleteFileData({
                        ...file,
                        footerFieldName: item?.name,
                        fieldName: item?.fileField,
                      });
                      setDeleteFooterFileModal(true);
                      return false;
                    }}
                    beforeUpload={(file) => {
                      const isPdf =
                        file?.type === "application/pdf" ||
                        file?.name?.toLowerCase()?.endsWith(".pdf");

                      if (!isPdf) {
                        toast.error("Only PDF files are allowed");
                        return Upload.LIST_IGNORE;
                      }

                      if (file?.size > 5 * 1024 * 1024) {
                        toast.error("PDF file size must not exceed 5MB");
                        return Upload.LIST_IGNORE;
                      }
                      return false; // Prevent auto upload
                    }}
                    onChange={({ fileList }) => {
                      footerDocumentForm.setFieldValue(
                        item.fileField,
                        fileList,
                      );
                    }}
                  >
                    <Button
                      className="saveBtn redBtn"
                      icon={<UploadOutlined />}
                    >
                      Upload PDF
                    </Button>
                  </Upload>
                </div>
              </Form.Item>
            )}
          </div>
        </div>
      ))}

      <div
        className="configureFormRow"
        style={{
          justifyContent: "flex-end",
        }}
      >
        <div className="configureFieldRow">
          <div className="configureFieldCol">
            <div className="form-btn-container">
              <Button
                type="button"
                className="clear-btn"
                style={{
                  marginRight: "10px",
                }}
                onClick={() => {
                  footerDocumentForm.resetFields();
                  setSelectedDocs({});
                }}
              >
                Cancel
              </Button>

              <Button
                className="saveBtn redBtn"
                htmlType="submit"
                disabled={isSubmitDisabled || loading}
                style={{
                  opacity: isSubmitDisabled || loading ? "0.5" : "1",
                }}
                loading={loading}
              >
                Submit
              </Button>
            </div>
          </div>
        </div>
      </div>

      <DeleteModal
        openModal={deleteFooterFileModal}
        message={
          <>
            Are you sure you want to delete{" "}
            <strong>{currentDeleteFileData?.label ?? ""}</strong> ?
          </>
        }
        yesClick={handleDeleteConfirm}
        closeModal={closeDeleteModal}
      />
    </Form>
  );
};

export default FooterDocumentSettings;
